import { CSSProperties, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  fetchStatistics,
  type Statistics,
  type StatisticsGruppenartSlice,
} from "@/api/statistics";
import { Btn } from "@/components/Btn";
import { Card } from "@/components/Card";
import { PageHeader } from "@/components/PageHeader";
import { useDateRange } from "@/lib/useDateRange";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import { formatEuro } from "@/lib/format";

const MONTH_DE_SHORT = ["Jan", "Feb", "Mrz", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];

const GRUPPENART_FARBE: Record<string, string> = {
  "BdP BaWü (Stämme oder LV)": "var(--cat-1)",
  "rdp (alle außer BdP)":      "var(--cat-2)",
  "Schule":                    "var(--cat-3)",
  "Diverse Jugendgruppen":     "var(--cat-4)",
  "Familie":                   "var(--cat-5)",
  "Erwachsene":                "var(--cat-6)",
  "Hochschule / Uni":          "var(--cat-7)",
  "(nicht angegeben)":         "var(--cat-unknown)",
};

const GRUPPENART_FARBE_POOL = [
  "var(--cat-8)",
  "var(--cat-9)",
  "var(--cat-10)",
  "var(--cat-11)",
  "var(--cat-12)",
];

function hashCode(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function farbeFuerGruppenart(name: string): string {
  const key = (name || "").trim();
  if (key in GRUPPENART_FARBE) return GRUPPENART_FARBE[key];
  return GRUPPENART_FARBE_POOL[hashCode(key) % GRUPPENART_FARBE_POOL.length];
}

function isoYearStart(): string {
  return `${new Date().getFullYear()}-01-01`;
}

function isoYearEnd(): string {
  return `${new Date().getFullYear()}-12-31`;
}

function formatInt(v: number): string {
  return Math.round(v).toLocaleString("de-DE");
}

function shortMonthLabel(label: string): string {
  // API liefert z. B. "Juli 2026" — verkürzen auf "Jul 26"
  const [name, year] = label.split(" ");
  const idx = ["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"].indexOf(name);
  const m = idx >= 0 ? MONTH_DE_SHORT[idx] : (name || "").slice(0, 3);
  return year ? `${m} ${year.slice(2)}` : m;
}

const KPI_LABEL: CSSProperties = {
  fontSize: 10,
  textTransform: "uppercase",
  letterSpacing: ".08em",
  color: "var(--fg-subtle)",
  fontWeight: 600,
};
const KPI_VALUE: CSSProperties = {
  fontSize: 30,
  color: "var(--brand-900)",
  marginTop: 6,
  fontVariantNumeric: "tabular-nums",
};

function KpiCard({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <div style={KPI_LABEL}>{label}</div>
      <div className="display" style={KPI_VALUE}>
        {value}
      </div>
    </Card>
  );
}

function MonthBars({ labels, counts }: { labels: string[]; counts: number[] }) {
  const max = Math.max(1, ...counts);
  if (!labels.length) {
    return <div style={{ color: "var(--fg-muted)", fontSize: 13 }}>Keine Buchungen im Zeitraum.</div>;
  }
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 8, height: 200, overflowX: "auto" }}>
      {labels.map((lab, i) => {
        const v = counts[i] ?? 0;
        const h = (v / max) * 150;
        const isMax = v === max && v > 0;
        return (
          <div
            key={`${lab}-${i}`}
            style={{
              flex: "1 1 0",
              minWidth: 36,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 4,
            }}
          >
            <div style={{ fontSize: 10, color: "var(--fg-muted)", fontWeight: 600 }}>{v}</div>
            <div
              style={{
                width: "100%",
                background: "var(--brand-700)",
                height: `${h}px`,
                borderRadius: "4px 4px 0 0",
                opacity: isMax ? 1 : 0.6,
                transition: "height .2s ease",
              }}
            />
            <div style={{ fontSize: 10, color: "var(--fg-muted)", whiteSpace: "nowrap" }}>
              {shortMonthLabel(lab)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function GruppenartBars({ data }: { data: StatisticsGruppenartSlice[] }) {
  const total = data.reduce((s, d) => s + d.value, 0);
  if (!data.length || total <= 0) {
    return <div style={{ color: "var(--fg-muted)", fontSize: 13 }}>Keine Daten.</div>;
  }
  const max = Math.max(...data.map((d) => d.value));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {data.map((d) => {
        const pct = (d.value / total) * 100;
        const w = (d.value / max) * 100;
        return (
          <div key={d.name} style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", gap: 12, alignItems: "center" }}>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 12,
                  color: "var(--fg)",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  marginBottom: 4,
                }}
                title={d.name}
              >
                {d.name}
              </div>
              <div
                style={{
                  height: 10,
                  background: "var(--bg-subtle, var(--bg))",
                  borderRadius: 999,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${w}%`,
                    height: "100%",
                    background: farbeFuerGruppenart(d.name),
                    borderRadius: 999,
                    transition: "width .2s ease",
                  }}
                />
              </div>
            </div>
            <div
              style={{
                fontSize: 12,
                color: "var(--fg-muted)",
                fontVariantNumeric: "tabular-nums",
                whiteSpace: "nowrap",
                textAlign: "right",
                minWidth: 90,
              }}
            >
              {formatInt(d.value)} <span style={{ color: "var(--fg-subtle)" }}>· {pct.toFixed(1)}%</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function UnitNightsTable({ rows }: { rows: Statistics["unit_nights_rows"] }) {
  if (!rows.length) {
    return <div style={{ color: "var(--fg-muted)", fontSize: 13 }}>Keine Einheiten im Zeitraum.</div>;
  }
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
      <thead>
        <tr style={{ borderBottom: "1px solid var(--border)" }}>
          <th
            style={{
              padding: "8px 10px",
              textAlign: "left",
              fontSize: 11,
              fontWeight: 700,
              color: "var(--fg-muted)",
              textTransform: "uppercase",
              letterSpacing: ".05em",
            }}
          >
            Einheit
          </th>
          <th
            style={{
              padding: "8px 10px",
              textAlign: "right",
              fontSize: 11,
              fontWeight: 700,
              color: "var(--fg-muted)",
              textTransform: "uppercase",
              letterSpacing: ".05em",
            }}
          >
            Übernachtungen
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={r.einheit} style={{ borderBottom: i < rows.length - 1 ? "1px solid var(--border)" : "none" }}>
            <td style={{ padding: "8px 10px" }}>{r.einheit}</td>
            <td style={{ padding: "8px 10px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
              {formatInt(r.n)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function CardTitle({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>{children}</div>;
}

const inputStyle: CSSProperties = {
  padding: "7px 10px",
  fontSize: 13,
  border: "1px solid var(--border)",
  borderRadius: 6,
  background: "var(--bg-card)",
  color: "var(--fg)",
  fontFamily: "var(--font-body)",
};

const labelStyle: CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: "var(--fg-muted)",
  textTransform: "uppercase",
  letterSpacing: ".05em",
  marginBottom: 4,
  display: "block",
};

export function StatistikPage() {
  useDocumentTitle("Statistik");
  const [von, setVon] = useState<string>(isoYearStart());
  const [bis, setBis] = useState<string>(isoYearEnd());
  const filterDateRange = useDateRange(
    von,
    bis,
    ({ from, to }) => { setVon(from); setBis(to); },
    { minNights: 0, keepDuration: false },
  );
  const [inclStorno, setInclStorno] = useState(false);

  // Effektive Werte werden erst beim Klick auf "Aktualisieren" gesetzt.
  const [applied, setApplied] = useState({ von: isoYearStart(), bis: isoYearEnd(), inclStorno: false });

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["statistics", applied.von, applied.bis, applied.inclStorno],
    queryFn: () => fetchStatistics({ von: applied.von, bis: applied.bis, inclStorno: applied.inclStorno }),
  });

  const apply = () => {
    if (bis < von) {
      window.alert("Das Ende-Datum liegt vor dem Beginn.");
      return;
    }
    setApplied({ von, bis, inclStorno });
  };

  const setCurrentYear = () => {
    const v = isoYearStart();
    const b = isoYearEnd();
    setVon(v);
    setBis(b);
    setInclStorno(false);
    setApplied({ von: v, bis: b, inclStorno: false });
  };

  const monthSlice = useMemo(() => {
    if (!data) return { labels: [], counts: [] };
    // Auf max. 12 Monate begrenzen — die letzten 12 (chronologisch sortiert von compute_statistics).
    const labels = data.month_labels.slice(-12);
    const counts = data.month_booking_counts.slice(-12);
    return { labels, counts };
  }, [data]);

  return (
    <div style={{ padding: "24px 32px" }}>
      <PageHeader
        title="Statistik"
        subtitle="Auslastung und Kennzahlen, Grundlage ist das Anreisedatum im gewählten Zeitraum."
      />

      {/* Filter-Leiste */}
      <Card>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-end" }}>
          <div>
            <label style={labelStyle}>Anreise ab</label>
            <input
              type="date"
              value={von}
              max={filterDateRange.fromProps.max}
              onChange={(e) => filterDateRange.setFrom(e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Anreise bis</label>
            <input
              type="date"
              value={bis}
              min={filterDateRange.toProps.min}
              onChange={(e) => filterDateRange.setTo(e.target.value)}
              style={inputStyle}
            />
          </div>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 13,
              color: "var(--fg)",
              cursor: "pointer",
              padding: "7px 0",
            }}
          >
            <input
              type="checkbox"
              checked={inclStorno}
              onChange={(e) => setInclStorno(e.target.checked)}
            />
            Stornierte einschließen
          </label>
          <div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
            <Btn variant="secondary" onClick={setCurrentYear}>
              Aktuelles Kalenderjahr
            </Btn>
            <Btn variant="primary" icon="refresh" onClick={apply}>
              Aktualisieren
            </Btn>
          </div>
        </div>
      </Card>

      {/* Status / Fehler */}
      {isLoading && (
        <div style={{ color: "var(--fg-muted)", fontSize: 14, padding: "32px 0", textAlign: "center" }}>
          Lade…
        </div>
      )}
      {isError && (
        <div
          style={{
            color: "#b91c1c",
            background: "#fef2f2",
            border: "1px solid #fecaca",
            borderRadius: 8,
            padding: "10px 14px",
            fontSize: 13,
            margin: "16px 0",
          }}
        >
          Fehler beim Laden der Statistik: {(error as Error)?.message || "unbekannt"}
        </div>
      )}

      {data && !isLoading && (
        <>
          {/* KPI-Grid: 6 Werte in 3 Spalten (responsiv) */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: 16,
              marginTop: 20,
              marginBottom: 20,
            }}
          >
            <KpiCard label="Buchungen" value={formatInt(data.n_bookings)} />
            <KpiCard label="Übernachtungen (Σ)" value={formatInt(data.n_guest_nights)} />
            <KpiCard label="Umsatz (inkl. Anzahlung)" value={formatEuro(data.revenue_sum)} />
            <KpiCard label="Davon Kurtaxe (abzuführen)" value={formatEuro(data.kurtaxe_sum)} />
            <KpiCard
              label="Ø Nächte / Buchung"
              value={data.n_bookings ? data.avg_nights_per_booking.toFixed(1) : "0"}
            />
            <KpiCard label="Erwachsene (Σ)" value={formatInt(data.n_adults_sum)} />
            <KpiCard label="Kinder (Σ)" value={formatInt(data.n_children_sum)} />
          </div>

          {/* Monatsdiagramm */}
          <Card>
            <CardTitle>Buchungen pro Monat (max. 12)</CardTitle>
            <MonthBars labels={monthSlice.labels} counts={monthSlice.counts} />
          </Card>

          {/* Einheiten-Tabelle + Gruppenart-Bars nebeneinander */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
              gap: 16,
              marginTop: 16,
            }}
          >
            <Card>
              <CardTitle>Übernachtungen je Einheit</CardTitle>
              <div style={{ fontSize: 11, color: "var(--fg-muted)", marginBottom: 8, marginTop: -8 }}>
                Mehrfachbuchung → dieselben Nächte je Einheit gezählt.
              </div>
              <UnitNightsTable rows={data.unit_nights_rows} />
            </Card>

            <Card>
              <CardTitle>Buchungen pro Gruppenart</CardTitle>
              <GruppenartBars data={data.gruppenart_bookings} />
            </Card>

            <Card>
              <CardTitle>Übernachtungen pro Gruppenart</CardTitle>
              <div style={{ fontSize: 11, color: "var(--fg-muted)", marginBottom: 8, marginTop: -8 }}>
                Σ (Nächte × Gästezahl) je Gruppenart.
              </div>
              <GruppenartBars data={data.gruppenart_nights} />
            </Card>
          </div>

          {!applied.inclStorno && data.storno_in_range_excluded > 0 && (
            <div
              style={{
                marginTop: 16,
                fontSize: 12,
                color: "var(--fg-muted)",
              }}
            >
              Im Zeitraum zusätzlich {data.storno_in_range_excluded} stornierte Buchung
              {data.storno_in_range_excluded === 1 ? "" : "en"} (ohne Haken nicht in den Zahlen oben).
            </div>
          )}
        </>
      )}
    </div>
  );
}
